import type { AppRouteConfig } from '../types';
import { Action } from '@/components/permissions/Action';
import { PermissionNameFor } from '@/components/permissions/Permission';
import { Resource } from '@/components/permissions/Resource';
import { serviceBusinessPaths } from '@/feature/service-business/data/serviceBusinessConfig';

const websiteViewPermission = PermissionNameFor(Resource.Website, Action.View);
const websiteUpdatePermission = PermissionNameFor(
  Resource.Website,
  Action.Update
);
const permission = (resource: string, action: string = Action.View) =>
  PermissionNameFor(resource, action);

export const platformServiceBusinessChildren = [
  {
    path: ':friendlyUrl/pages/:pageSlug',
    element: 'LegacyWebsitePageRedirect'
  },
  { path: ':friendlyUrl/blog/:slug', element: 'ServiceBusinessPublicPage' },
  { path: ':friendlyUrl/blog', element: 'ServiceBusinessPublicPage' },
  { path: 'blog/:slug', element: 'ServiceBusinessPublicPage' },
  { path: 'blog', element: 'ServiceBusinessPublicPage' },
  { path: ':friendlyUrl/:pageSlug', element: 'PublicWebsitePage' },
  { path: ':friendlyUrl', element: 'PublicWebsitePage' }
];

export const tenantPublicServiceBusinessChildren = [
  { path: null, element: 'PublicWebsitePage', index: true },
  { path: 'blog', element: 'ServiceBusinessPublicPage' },
  { path: 'blog/:slug', element: 'ServiceBusinessPublicPage' },
  { path: ':pageSlug', element: 'PublicWebsitePage' }
];

export const backofficeServiceBusinessChildren = [
  {
    path: null,
    element: 'ServiceBusinessBackofficePage',
    index: true,
    requiredPermission: permission(Resource.Dashboard)
  },
  {
    path: 'calendar',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.CalendarCompany)
  },
  {
    path: 'inventory',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Inventory)
  },
  {
    path: 'my-drive',
    element: 'MyDrivePage',
    requiredPermission: permission(Resource.DriveFiles)
  },
  {
    path: 'orders',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.ServiceOrders, Action.Search)
  },
  {
    path: 'orders/create',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.ServiceOrders, Action.Create)
  },
  {
    path: 'orders/:orderId',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.ServiceOrders, Action.View)
  },
  {
    path: 'order-settings',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.OrderStatuses, Action.View)
  },
  {
    path: 'customers',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Customers, Action.Search)
  },
  {
    path: 'customers/:customerId',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Customers)
  },
  {
    path: 'employees',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Employees, Action.Search)
  },
  {
    path: 'employees/:employeeId',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Employees)
  },
  {
    path: 'reports',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Analytics)
  },
  {
    path: 'messenger',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Messenger)
  },
  {
    path: 'sms',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Reminders, Action.Search)
  },
  {
    path: 'website',
    element: 'WebsiteBuilderAdminPage',
    requiredPermission: websiteUpdatePermission
  },
  {
    path: 'website/pages',
    element: 'WebsiteBuilderAdminPage',
    requiredPermission: websiteViewPermission
  },
  {
    path: 'website/menus',
    element: 'WebsiteBuilderMenusPage',
    requiredPermission: websiteUpdatePermission
  },
  {
    path: 'website/forms',
    element: 'WebsiteBuilderFormsPage',
    requiredPermission: websiteViewPermission
  },
  {
    path: 'website/pages/:pageId/editor',
    element: 'WebsiteBuilderEditorPage',
    requiredPermission: websiteUpdatePermission
  },
  {
    path: 'settings',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Settings)
  },
  {
    path: 'pages',
    element: 'WebsiteBuilderPagesEntryPage',
    requiredPermission: websiteViewPermission
  },
  {
    path: 'pages/create',
    element: 'WebsiteBuilderPagesEntryPage',
    requiredPermission: permission(Resource.Website, Action.Create)
  },
  {
    path: 'blog-seo',
    element: 'ServiceBusinessBackofficePage',
    requiredPermission: permission(Resource.Blog)
  }
];

export const hsdeskRoutes: AppRouteConfig[] = [
  {
    path: '/',
    layout: 'HomepageLayout',
    children: [{ path: null, element: 'LandingPage', index: true }]
  },
  {
    path: serviceBusinessPaths.publicBasePath,
    layout: 'EmptyLayout',
    children: platformServiceBusinessChildren
  },
  {
    path: `${serviceBusinessPaths.backofficeBasePath}/website/preview`,
    layout: 'EmptyLayout',
    element: 'ProtectedRoute',
    children: [
      {
        path: null,
        element: 'WebsiteBuilderPreviewEntryPage',
        index: true
      }
    ]
  },
  {
    path: `${serviceBusinessPaths.backofficeBasePath}/website/local-preview/:previewId`,
    layout: 'EmptyLayout',
    children: [
      {
        path: null,
        element: 'WebsiteBuilderPreviewPage',
        index: true
      }
    ]
  },
  {
    path: `${serviceBusinessPaths.backofficeBasePath}/website/pages/:pageId/preview`,
    layout: 'EmptyLayout',
    element: 'ProtectedRoute',
    children: [
      {
        path: null,
        element: 'WebsiteBuilderPreviewPage',
        index: true
      }
    ]
  },
  {
    path: serviceBusinessPaths.backofficeBasePath,
    layout: 'MainLayout',
    element: 'ProtectedRoute',
    children: backofficeServiceBusinessChildren
  },
  {
    path: `${serviceBusinessPaths.backofficeBasePath}/website-preview`,
    layout: 'EmptyLayout',
    element: 'ProtectedRoute',
    children: [
      {
        path: null,
        element: 'WebsiteBuilderPreviewEntryPage',
        index: true
      }
    ]
  },
  {
    path: '/client',
    layout: 'EmptyLayout',
    element: 'ProtectedRoute',
    children: [{ path: null, element: 'CustomerPortalPage', index: true }]
  },
  {
    path: '/customer',
    layout: 'EmptyLayout',
    element: 'ProtectedRoute',
    children: [{ path: null, element: 'CustomerPortalPage', index: true }]
  },
  {
    path: '/order-status/:token',
    layout: 'EmptyLayout',
    children: [{ path: null, element: 'PublicOrderViewPage', index: true }]
  },
  {
    path: '/order/:orderId',
    layout: 'EmptyLayout',
    children: [{ path: null, element: 'PublicOrderViewPage', index: true }]
  }
];
